import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import socketService from '../services/socket';
import { FiSend, FiPaperclip, FiImage, FiCheck, FiCheckCircle } from 'react-icons/fi';
import { UserAvatar, AuthenticatedImage } from '../components/OptimizedImage';
import './Messages.css';

const Messages = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Connect to WebSocket on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      socketService.connect(token);

      // Listen for new messages
      socketService.on('message_received', handleNewMessage);
      socketService.on('message_sent', handleMessageSent);
      socketService.on('messages_read', handleMessagesRead);
      socketService.on('user_typing', handleUserTyping);
      socketService.on('user_stopped_typing', handleUserStoppedTyping);
    }

    fetchConversations();

    return () => {
      // Cleanup socket listeners
      socketService.off('message_received');
      socketService.off('message_sent');
      socketService.off('messages_read');
      socketService.off('user_typing');
      socketService.off('user_stopped_typing');
    };
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.user.id);
      // Mark messages as read when conversation is opened
      console.log('📤 Marking messages as read from:', selectedConversation.user.id);
      socketService.markAsRead(selectedConversation.user.id);
    }
  }, [selectedConversation]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/api/messages/conversations');
      setConversations(response.data.conversations);

      // Check if there's a userId in the URL params to start a new conversation
      const userIdParam = searchParams.get('userId');

      if (userIdParam) {
        // Check if conversation with this user already exists
        const existingConv = response.data.conversations.find(
          conv => conv.user.id === parseInt(userIdParam)
        );

        if (existingConv) {
          setSelectedConversation(existingConv);
        } else {
          // Fetch user data to start a new conversation
          try {
            const userResponse = await api.get(`/api/users/${userIdParam}`);
            setSelectedConversation({
              user: userResponse.data.user,
              unread_count: 0,
              last_message: null
            });
            setMessages([]);
          } catch (error) {
            console.error('Error fetching user:', error);
            toast.error(t('messages.userNotFound'));
          }
        }
      } else if (response.data.conversations.length > 0) {
        setSelectedConversation(response.data.conversations[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await api.get(`/api/messages/conversation/${userId}`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // WebSocket event handlers
  const handleNewMessage = (message) => {
    console.log('📨 New message received:', message);
    // Add message to list if it's from current conversation
    if (selectedConversation &&
        (message.sender.id === selectedConversation.user.id ||
         message.receiver.id === selectedConversation.user.id)) {
      setMessages(prev => [...prev, message]);

      // Mark as read if we're viewing the conversation
      if (message.sender.id === selectedConversation.user.id) {
        console.log('📤 Auto-marking message as read from:', message.sender.id);
        socketService.markAsRead(message.sender.id);
      }
    }

    // Update conversations list
    fetchConversations();
  };

  const handleMessageSent = (message) => {
    console.log('✅ Message sent confirmation:', message);
    // Replace optimistic message or add new message
    if (selectedConversation && message.receiver.id === selectedConversation.user.id) {
      setMessages(prev => {
        // Check if there's an optimistic message to replace
        const hasOptimistic = prev.some(msg => msg._isOptimistic);
        if (hasOptimistic) {
          console.log('🔄 Replacing optimistic message with real one');
          // Replace the most recent optimistic message
          const lastOptimisticIndex = prev.map(msg => msg._isOptimistic).lastIndexOf(true);
          return prev.map((msg, idx) => idx === lastOptimisticIndex ? message : msg);
        }
        // Otherwise just add the message
        console.log('➕ Adding new message to list');
        return [...prev, message];
      });
    }
  };

  const handleMessagesRead = ({ message_ids }) => {
    console.log('📖 Messages marked as read:', message_ids);
    // Update read status for messages
    setMessages(prev => prev.map(msg =>
      message_ids.includes(msg.id) ? { ...msg, is_read: true } : msg
    ));
  };

  const handleUserTyping = ({ sender_id }) => {
    console.log('⌨️ User typing:', sender_id);
    if (selectedConversation && sender_id === selectedConversation.user.id) {
      console.log('✅ Showing typing indicator');
      setIsTyping(true);
    }
  };

  const handleUserStoppedTyping = ({ sender_id }) => {
    console.log('⌨️ User stopped typing:', sender_id);
    if (selectedConversation && sender_id === selectedConversation.user.id) {
      console.log('✅ Hiding typing indicator');
      setIsTyping(false);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Send typing indicator
    if (selectedConversation) {
      console.log('📤 Sending typing indicator to:', selectedConversation.user.id);
      socketService.startTyping(selectedConversation.user.id);

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        console.log('📤 Sending stop typing indicator to:', selectedConversation.user.id);
        socketService.stopTyping(selectedConversation.user.id);
      }, 3000);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAuthenticatedDownload = async (fileUrl, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseURL}${fileUrl}`;

      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error(t('messages.failedToDownload'));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    // Stop typing indicator
    if (selectedConversation) {
      socketService.stopTyping(selectedConversation.user.id);
    }

    // Get current user info from localStorage
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // Create optimistic message
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: newMessage.trim() || null,
      sender: {
        id: currentUser.id,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name
      },
      receiver: {
        id: selectedConversation.user.id,
        first_name: selectedConversation.user.first_name,
        last_name: selectedConversation.user.last_name
      },
      created_at: new Date().toISOString(),
      is_read: false,
      attachment_url: null,
      attachment_name: selectedFile ? selectedFile.name : null,
      attachment_type: selectedFile ? (selectedFile.type.startsWith('image/') ? 'image' : 'file') : null,
      _isOptimistic: true
    };

    // Add message immediately to UI (optimistic update)
    setMessages(prev => [...prev, optimisticMessage]);

    const messageContent = newMessage.trim();
    const fileToSend = selectedFile;

    // Clear input immediately
    setNewMessage('');
    clearFile();

    try {
      if (fileToSend) {
        // Send with file
        const formData = new FormData();
        formData.append('receiver_id', selectedConversation.user.id);
        if (messageContent) {
          formData.append('content', messageContent);
        }
        formData.append('file', fileToSend);

        const response = await api.post('/api/messages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Replace optimistic message with real one
        setMessages(prev => prev.map(msg =>
          msg.id === optimisticMessage.id ? response.data.data : msg
        ));
      } else {
        // Send text only via WebSocket
        socketService.sendMessage(selectedConversation.user.id, messageContent);
      }
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      toast.error(t('messages.failedToSend'));
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="messages-page">
      <div className="messages-container">
        {/* Conversations List */}
        <div className="conversations-sidebar">
          <h2>{t('messages.title')}</h2>
          {conversations.length === 0 ? (
            <p className="no-conversations">{t('messages.noConversations')}</p>
          ) : (
            <div className="conversations-list">
              {conversations.map((conv) => (
                <div
                  key={conv.user.id}
                  className={`conversation-item ${selectedConversation?.user.id === conv.user.id ? 'active' : ''}`}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="conv-avatar">
                    {conv.user.avatar ? (
                      <UserAvatar
                        src={conv.user.avatar}
                        alt={conv.user.first_name}
                        size={48}
                      />
                    ) : (
                      conv.user.first_name[0]
                    )}
                  </div>
                  <div className="conv-info">
                    <h4>{conv.user.first_name} {conv.user.last_name}</h4>
                    <p>{conv.last_message?.content ? conv.last_message.content.substring(0, 50) + '...' : conv.last_message?.attachment_name || t('messages.noMessages')}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="unread-badge">{conv.unread_count}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className="messages-main">
          {selectedConversation ? (
            <>
              <div className="messages-header">
                <div className="header-user">
                  <div className="user-avatar">
                    {selectedConversation.user.avatar ? (
                      <UserAvatar
                        src={selectedConversation.user.avatar}
                        alt={selectedConversation.user.first_name}
                        size={40}
                      />
                    ) : (
                      selectedConversation.user.first_name[0]
                    )}
                  </div>
                  <h3>{selectedConversation.user.first_name} {selectedConversation.user.last_name}</h3>
                </div>
              </div>

              <div className="messages-list">
                {messages.map((message) => {
                  const isSent = message.sender.id !== selectedConversation.user.id;
                  return (
                    <div
                      key={message.id}
                      className={`message ${isSent ? 'sent' : 'received'}`}
                    >
                      {message.attachment_url && (
                        <div className="message-attachment">
                          {message.attachment_type === 'image' ? (
                            <AuthenticatedImage
                              src={message.attachment_url}
                              alt={message.attachment_name}
                              className="message-image"
                              onClick={() => window.open(message.attachment_url, '_blank')}
                            />
                          ) : (
                            <button
                              onClick={() => handleAuthenticatedDownload(message.attachment_url, message.attachment_name)}
                              className="message-file"
                            >
                              <FiPaperclip />
                              {message.attachment_name}
                            </button>
                          )}
                        </div>
                      )}
                      {message.content && (
                        <div className="message-content">{message.content}</div>
                      )}
                      <div className="message-footer">
                        <span className="message-time">
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {isSent && (
                          <span className="message-status">
                            {message.is_read ? (
                              <FiCheckCircle className="read-icon" />
                            ) : (
                              <FiCheck className="sent-icon" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {isTyping && (
                  <div className="typing-indicator">
                    <span className="typing-text">{selectedConversation.user.first_name} {t('messages.isTyping')}</span>
                    <div className="typing-dots">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendMessage} className="message-input-form">
                {filePreview && (
                  <div className="file-preview">
                    <img src={filePreview} alt="Preview" />
                    <button type="button" onClick={clearFile} className="clear-file">
                      ×
                    </button>
                  </div>
                )}
                {selectedFile && !filePreview && (
                  <div className="file-preview-text">
                    <FiPaperclip />
                    {selectedFile.name}
                    <button type="button" onClick={clearFile} className="clear-file">
                      ×
                    </button>
                  </div>
                )}
                <div className="input-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    accept="image/*,application/pdf,.doc,.docx"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn btn-secondary attachment-btn"
                    title={t('messages.attachFile')}
                  >
                    <FiPaperclip />
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder={t('messages.typeMessage')}
                    className="message-input"
                  />
                  <button type="submit" className="btn btn-primary" disabled={!newMessage.trim() && !selectedFile}>
                    <FiSend />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="no-conversation-selected">
              <p>{t('messages.selectConversation')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
